import React, { useEffect } from "react";
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next';
import { formValueSelector } from 'redux-form';
import { loadLayouts, loadSetting } from '../../redux';
import { Link } from 'react-router-dom';
import { PdfLayoutsField } from '../../components';
import PdfLayoutSampleButton from '../PdfLayoutSampleButton';
import PdfLayoutLegacyWarning from '../PdfLayoutLegacyWarning';
import CardIconAction from '../CardIconAction/CardIconAction';
import { Card, CardBody, CardTitle } from "reactstrap";
import * as _ from 'es-toolkit';
import {
    resolveTickettypePdfLayoutId,
    resolveLayoutDisplayName,
    showsPdfTicketLegacyWarning,
    pdf_layout_edit_path,
} from '../../services/pdfLayoutSettingsUtils';
import { EMPTY_OBJECT, Utils } from '../../services';
import { sort_layouts_by_localized_name } from '../../services/layoutListSort';
import { resolve_preview_pricing } from '../../services/pdfLayoutPreviewSample';

import './PdfLayoutMappings.css';
import '../CardIconAction/CardIconAction.scss';
import '../EmailLayoutsField/EmailLayoutsField.scss';

const tickettypeFormSelector = formValueSelector('tickettypeForm');

const OverrideRemoveButton = ({ id, title, onClick }) => (
    <span className="CardIconActions CardIconActions--inline CardIconActions--inline-start mb-0">
        <CardIconAction
            id={id}
            icon="fa-trash"
            danger
            title={title}
            onClick={onClick}
        />
    </span>
);

const PdfLayoutMappings = ({
    name,
    type,
    removeLayout,
    id,
    pricingNames,
    pricingName,
    tickettypeId,
    hasOverrides = false,
}) => {
    const storedLayouts = useSelector(state => state.layouts.layouts);
    const setting = useSelector(state => state.settings.setting);
    const { tickettype } = useSelector(state => state.tickettypes);
    const { pricinglists } = useSelector(state => state.pricinglists);
    const overrideLayouts = useSelector(state => tickettypeFormSelector(state, name) || EMPTY_OBJECT);

    const dispatch    = useDispatch();
    const { t, i18n } = useTranslation();

    useEffect(() => {
        dispatch(loadLayouts());
        dispatch(loadSetting('default'));
    }, [dispatch]);

    const layouts = React.useMemo(() => {
        return sort_layouts_by_localized_name(
            (storedLayouts || []).filter(l => l.layout_type === 'pdf' && l.type === type),
            i18n.language,
        );
    }, [type, storedLayouts, i18n.language]);

    const layoutOptions = (target, effectiveId, configuredId) => {
        return layouts?.filter(l => l.target === target).map(layout => {
            const option = {
                key: layout._id,
                value: layout._id,
                text: Utils.localized_or_fallback(
                    layout.name,
                    i18n.language,
                    Utils.default_lang()
                ),
            };
            if (!configuredId && effectiveId && layout._id === effectiveId) {
                option.description = t('settings.default_layouts.pin_to_keep');
            }
            return option;
        });
    };

    const targets = React.useMemo(() => _.uniq(layouts.map(l => l.target)), [layouts]);

    const layoutName = (layoutId) => resolveLayoutDisplayName(
        layoutId,
        { pdfLayouts: storedLayouts },
        i18n.language,
        Utils.default_lang()
    );

    const onRemoveLayout = () => {
        removeLayout(id);
    }

    const names = (pricingNames?.length
        ? pricingNames
        : (pricingName ? [pricingName] : [])
    ).filter(Boolean);
    const title = id
        ? t('layouts.on_pricing', { count: names.length || 1, pricing: names.join(', ') || '-' })
        : (hasOverrides ? t('layouts.on_other_pricings') : t('layouts.on_any_pricing'));
    const samplePricing = id
        ? resolve_preview_pricing(tickettype, id, pricinglists)
        : null;
    const modelLinkContext = {
        tickettypeId,
        pricingKey: id || undefined,
    };

    return (
        <div className="PdfLayoutMappings">
            <Card className="CardShell mb-3">
                <CardBody>
                    <CardTitle className="d-flex align-items-center justify-content-start w-100 text-left">
                        {removeLayout && (
                            <OverrideRemoveButton
                                id={`delete-pdf-layout-${id || 'default'}`}
                                title={t('common.card_action_delete_pdf_override')}
                                onClick={onRemoveLayout}
                            />
                        )}
                        <span>{title}</span>
                    </CardTitle>
                    <div className="PdfLayoutMappings-targets">
                        {targets?.map(target => {
                            const configuredId = overrideLayouts?.[target] || null;
                            const effectiveId = resolveTickettypePdfLayoutId({
                                setting,
                                tickettypeId,
                                overrideLayouts,
                                target,
                            });
                            const kind = tickettypeId === 'one-time-pass' ? 'one_time_pass' : 'ticket';
                            const modelPath = effectiveId
                                ? pdf_layout_edit_path(effectiveId, modelLinkContext)
                                : null;

                            return (
                            <div key={target} className="PdfLayoutMappings-target">
                                <div className="PdfLayoutMappings-target-header">
                                    <div className="PdfLayoutMappings-target-label">
                                        {t(`layouts.target_${target}`)}
                                    </div>
                                    {(configuredId || effectiveId) && (
                                        <div className="EmailLayoutsField-actions">
                                            {modelPath && (
                                                <Link
                                                    to={modelPath}
                                                    className="EmailLayoutsField-action btn btn-sm btn-secondary"
                                                    title={t('layouts.references.open_layout')}
                                                >
                                                    <i className="fa fa-external-link" aria-hidden="true" />
                                                    &nbsp;
                                                    {t('settings.default_layouts.open_model')}
                                                </Link>
                                            )}
                                            {effectiveId && (
                                                <PdfLayoutSampleButton
                                                    layoutId={effectiveId}
                                                    storedLayouts={storedLayouts}
                                                    lang={i18n.language}
                                                    tickettypeId={tickettypeId}
                                                    pricingKey={id || undefined}
                                                    pricing={samplePricing}
                                                />
                                            )}
                                        </div>
                                    )}
                                </div>
                                <PdfLayoutsField
                                    name={`${name}.${target}`}
                                    options={layoutOptions(target, effectiveId, configuredId)}
                                    placeholder={
                                        !configuredId && effectiveId
                                            ? t('layouts.inherit_default', { name: layoutName(effectiveId) })
                                            : t('layouts.please_choose_a_layout')
                                    }
                                    show_edit_link={false}
                                />
                                {!configuredId && !effectiveId && showsPdfTicketLegacyWarning(kind, target) && (
                                    <PdfLayoutLegacyWarning />
                                )}
                                {!configuredId && !effectiveId && !showsPdfTicketLegacyWarning(kind, target) && (
                                    <div className="mt-2 small PdfLayoutMappings-effective">
                                        {t('settings.default_layouts.effective_legacy')}
                                    </div>
                                )}
                            </div>
                            );
                        })}
                    </div>
                </CardBody>
            </Card>
        </div>
    );
}

export default PdfLayoutMappings;
